import type { OutputLayer } from '@/components/preview/output-layer-preview';
import { LiveContextFocusMode } from '@/enums/live-context-focus-mode.enum';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { LivePlayStateEntry, LiveContextPlayStateWsPayloadV1 } from '@/state/player/live-play-state-ws.store';
import {
  contentLayerHasUsableSource,
  isLookAhead2ByPlayerKey,
  isLookAheadByPlayerKey,
  isLookAheadPrevByPlayerKey,
  isLookAheadResumeByPlayerKey,
  isOperatorPlayStateByPlayerKey,
  isPreviewByPlayerKey,
  preserveContentMediaFromCurrent,
} from '@/helpers/resolve-output-preview-layers';

type ByPlayer = NonNullable<NonNullable<LiveContextStateDto['playState']>['byPlayer']>;

/** Higher seq wins. GET and WS both stamp a boolean once the switch exists. */
function pickBackupSlides(
  getSeq: number,
  wsSeq: number | undefined,
  getBit: boolean | undefined,
  wsBit: boolean | undefined,
): boolean | undefined {
  if (typeof wsBit === 'boolean' && typeof wsSeq === 'number' && wsSeq >= getSeq) {
    return wsBit;
  }
  if (typeof getBit === 'boolean') {
    return getBit;
  }
  return wsBit;
}

/** True when Control Current `_preview:*` already paints a visible media-agent LiveKit row. */
export function operatorPreviewShowsVisibleAgentLiveKit(byPlayer: ByPlayer | undefined): boolean {
  if (!byPlayer) {
    return false;
  }
  for (const [key, entry] of Object.entries(byPlayer)) {
    if (!isPreviewByPlayerKey(key)) {
      continue;
    }
    for (const layer of entry?.layers ?? []) {
      if (layer.visible === false) {
        continue;
      }
      if (
        layer.componentId === 'livekitTrackMediaAgent' ||
        (typeof layer.itemId === 'string' && layer.itemId.startsWith('mediaAgent:'))
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Stable identity of operator Current + Next rows the Control UI is painted from. */
export function previewPlayStateSignature(byPlayer: ByPlayer | undefined): string {
  if (!byPlayer) {
    return '';
  }
  return Object.keys(byPlayer)
    .filter(isOperatorPlayStateByPlayerKey)
    .sort()
    .map((key) => {
      const layers = byPlayer[key]?.layers ?? [];
      return `${key}=${layers
        .map(
          (layer) =>
            `${layer.layerId}:${layer.itemId ?? ''}:${layer.visible === false ? 0 : 1}:${layer.trackSid ?? ''}`,
        )
        .join(',')}`;
    })
    .join('|');
}

function stripMatchingLookAheadKeys(byPlayer: ByPlayer, match: (key: string) => boolean): ByPlayer {
  const next: ByPlayer = { ...byPlayer };
  for (const key of Object.keys(next)) {
    if (match(key)) {
      delete next[key];
    }
  }
  return next;
}

function stripLookAheadKeys(byPlayer: ByPlayer): ByPlayer {
  return stripMatchingLookAheadKeys(byPlayer, isLookAheadByPlayerKey);
}

type LookAheadCueIdentity = {
  itemId?: string;
  scope?: string;
  slotId?: string;
};

/** Next cue row identity — item + playlist context. ShowMode is an overlay, not a new cue. */
function lookAheadCueIdentityChanged(next: LookAheadCueIdentity, prev: LookAheadCueIdentity | undefined): boolean {
  if (!next.itemId) {
    return false;
  }
  return (
    next.itemId !== prev?.itemId ||
    (next.scope ?? '') !== (prev?.scope ?? '') ||
    (next.slotId ?? '') !== (prev?.slotId ?? '')
  );
}

function overlayWsPreviewOntoGet(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry,
): LiveContextStateDto {
  const getByPlayer = liveContextState?.playState?.byPlayer ?? {};
  const lookAheadIdentityChanged = lookAheadCueIdentityChanged(
    { itemId: wsEntry.lookAheadItemId, scope: wsEntry.lookAheadScope, slotId: wsEntry.lookAheadSlotId },
    liveContextState?.playState?.lookAheadPreview,
  );
  let byPlayer: ByPlayer = lookAheadIdentityChanged
    ? { ...stripLookAheadKeys({ ...getByPlayer }), ...wsEntry.byPlayer }
    : { ...getByPlayer, ...wsEntry.byPlayer };

  let lookAheadPreview = liveContextState?.playState?.lookAheadPreview;
  if (wsEntry.lookAheadItemId === '') {
    byPlayer = stripLookAheadKeys(byPlayer);
    lookAheadPreview = undefined;
  } else if (wsEntry.lookAheadItemId) {
    lookAheadPreview = {
      itemId: wsEntry.lookAheadItemId,
      showMode: wsEntry.lookAheadShowMode,
      scope: wsEntry.lookAheadScope,
      slotId: wsEntry.lookAheadSlotId,
    };
  }

  let lookAheadPreview2 = liveContextState?.playState?.lookAheadPreview2;
  if (wsEntry.lookAhead2ItemId === '') {
    byPlayer = stripMatchingLookAheadKeys(byPlayer, isLookAhead2ByPlayerKey);
    lookAheadPreview2 = undefined;
  } else if (wsEntry.lookAhead2ItemId) {
    lookAheadPreview2 = {
      itemId: wsEntry.lookAhead2ItemId,
      showMode: wsEntry.lookAhead2ShowMode,
      scope: wsEntry.lookAhead2Scope,
      slotId: wsEntry.lookAhead2SlotId,
    };
  }

  let lookAheadInsertResume = liveContextState?.playState?.lookAheadInsertResume;
  if (wsEntry.lookAheadInsertResumeItemId === '') {
    byPlayer = stripMatchingLookAheadKeys(byPlayer, isLookAheadResumeByPlayerKey);
    lookAheadInsertResume = undefined;
  } else if (wsEntry.lookAheadInsertResumeItemId) {
    lookAheadInsertResume = {
      itemId: wsEntry.lookAheadInsertResumeItemId,
      showMode: wsEntry.lookAheadInsertResumeShowMode,
      scope: wsEntry.lookAheadInsertResumeScope,
      slotId: wsEntry.lookAheadInsertResumeSlotId,
    };
  }

  let lookAheadPrevious = liveContextState?.playState?.lookAheadPrevious;
  if (wsEntry.lookAheadPrevItemId === '') {
    byPlayer = stripMatchingLookAheadKeys(byPlayer, isLookAheadPrevByPlayerKey);
    lookAheadPrevious = undefined;
  } else if (wsEntry.lookAheadPrevItemId) {
    lookAheadPrevious = {
      itemId: wsEntry.lookAheadPrevItemId,
      showMode: wsEntry.lookAheadPrevShowMode,
      scope: wsEntry.lookAheadPrevScope,
      slotId: wsEntry.lookAheadPrevSlotId,
    };
  }

  // Hide emits `inFlight: true` even if `_preview:*` still has a visible outgoing agent track.
  // Honor that flag so Control Current can hide like players. Only infer "revealed" when the
  // payload omits the flag and the preview already shows the agent.
  const wsShowsVisibleAgent = operatorPreviewShowsVisibleAgentLiveKit(wsEntry.byPlayer);
  const nextInFlight =
    typeof wsEntry.presentationTransitionInFlight === 'boolean'
      ? wsEntry.presentationTransitionInFlight
      : wsShowsVisibleAgent
        ? false
        : liveContextState?.playState?.presentationTransitionInFlight;

  return {
    ...liveContextState,
    playState: {
      ...liveContextState?.playState,
      byPlayer,
      showMode: wsEntry.showMode ?? liveContextState?.playState?.showMode,
      // Taken as-is rather than with a fallback: the websocket reports the deck that belongs to these
      // layers, and "no deck on air" is a real value. Falling back to the older GET would keep the
      // slide controls on screen after the deck left the air.
      activeAgentDeckMediaId: wsEntry.activeAgentDeckMediaId,
      lookAheadPreview,
      lookAheadPreview2,
      lookAheadInsertResume,
      lookAheadPrevious,
      presentationTransitionInFlight: nextInFlight,
      backupSlides: pickBackupSlides(
        liveContextState?.playState?.seq ?? 0,
        wsEntry.seq,
        liveContextState?.playState?.backupSlides,
        wsEntry.backupSlides,
      ),
    },
  };
}

/**
 * Prefer the `live-context-play-state` websocket snapshot for operator `_preview:*` /
 * `_lookahead:*` layers until GET catches up.
 *
 * The websocket is emitted at the same moment players receive `show`, *before* Mongo persist. A GET
 * that completes afterwards can still carry the previous on-air layers. Comparing client clocks
 * (`dataUpdatedAt` vs `receivedAt`) therefore rolls the preview back — which is why the UI looked
 * stuck until a later cue-list click forced a post-commit refetch.
 *
 * When signatures match, GET is used as-is (it also carries physical player rows). A stale WS is
 * cleared after the post-command live-context refetch (see `useCueCommandMutation`).
 */
export function applyLivePlayStateWsPayloadToEntry(
  prev: LivePlayStateEntry | undefined,
  payload: LiveContextPlayStateWsPayloadV1,
  receivedAt = Date.now(),
): LivePlayStateEntry {
  const incomingTouchesPreview = Object.keys(payload.byPlayer ?? {}).some(isPreviewByPlayerKey);
  let prevByPlayer: ByPlayer = { ...(prev?.byPlayer ?? {}) };
  if (
    payload.lookAheadItemId !== undefined &&
    lookAheadCueIdentityChanged(
      { itemId: payload.lookAheadItemId, scope: payload.lookAheadScope, slotId: payload.lookAheadSlotId },
      { itemId: prev?.lookAheadItemId, scope: prev?.lookAheadScope, slotId: prev?.lookAheadSlotId },
    )
  ) {
    prevByPlayer = stripLookAheadKeys(prevByPlayer);
  }
  if (payload.lookAheadItemId === '') {
    prevByPlayer = stripLookAheadKeys(prevByPlayer);
  }
  return {
    receivedAt,
    byPlayer: { ...prevByPlayer, ...(payload.byPlayer ?? {}) },
    showMode: incomingTouchesPreview ? payload.showMode : (payload.showMode ?? prev?.showMode),
    activeAgentDeckMediaId: incomingTouchesPreview
      ? payload.activeAgentDeckMediaId
      : (payload.activeAgentDeckMediaId ?? prev?.activeAgentDeckMediaId),
    lookAheadItemId: payload.lookAheadItemId !== undefined ? payload.lookAheadItemId : prev?.lookAheadItemId,
    lookAheadShowMode: payload.lookAheadItemId !== undefined ? payload.lookAheadShowMode : prev?.lookAheadShowMode,
    lookAheadScope: payload.lookAheadItemId !== undefined ? payload.lookAheadScope : prev?.lookAheadScope,
    lookAheadSlotId: payload.lookAheadItemId !== undefined ? payload.lookAheadSlotId : prev?.lookAheadSlotId,
    lookAhead2ItemId: payload.lookAhead2ItemId !== undefined ? payload.lookAhead2ItemId : prev?.lookAhead2ItemId,
    lookAhead2ShowMode: payload.lookAhead2ItemId !== undefined ? payload.lookAhead2ShowMode : prev?.lookAhead2ShowMode,
    lookAhead2Scope: payload.lookAhead2ItemId !== undefined ? payload.lookAhead2Scope : prev?.lookAhead2Scope,
    lookAhead2SlotId: payload.lookAhead2ItemId !== undefined ? payload.lookAhead2SlotId : prev?.lookAhead2SlotId,
    lookAheadInsertResumeItemId:
      payload.lookAheadInsertResumeItemId !== undefined
        ? payload.lookAheadInsertResumeItemId
        : prev?.lookAheadInsertResumeItemId,
    lookAheadInsertResumeShowMode:
      payload.lookAheadInsertResumeItemId !== undefined
        ? payload.lookAheadInsertResumeShowMode
        : prev?.lookAheadInsertResumeShowMode,
    lookAheadInsertResumeScope:
      payload.lookAheadInsertResumeItemId !== undefined
        ? payload.lookAheadInsertResumeScope
        : prev?.lookAheadInsertResumeScope,
    lookAheadInsertResumeSlotId:
      payload.lookAheadInsertResumeItemId !== undefined
        ? payload.lookAheadInsertResumeSlotId
        : prev?.lookAheadInsertResumeSlotId,
    lookAheadPrevItemId:
      payload.lookAheadPrevItemId !== undefined ? payload.lookAheadPrevItemId : prev?.lookAheadPrevItemId,
    lookAheadPrevShowMode:
      payload.lookAheadPrevItemId !== undefined ? payload.lookAheadPrevShowMode : prev?.lookAheadPrevShowMode,
    lookAheadPrevScope:
      payload.lookAheadPrevItemId !== undefined ? payload.lookAheadPrevScope : prev?.lookAheadPrevScope,
    lookAheadPrevSlotId:
      payload.lookAheadPrevItemId !== undefined ? payload.lookAheadPrevSlotId : prev?.lookAheadPrevSlotId,
    presentationTransitionInFlight: incomingTouchesPreview
      ? payload.presentationTransitionInFlight
      : (payload.presentationTransitionInFlight ?? prev?.presentationTransitionInFlight),
    backupSlides: typeof payload.backupSlides === 'boolean' ? payload.backupSlides : prev?.backupSlides,
    focusMode: payload.focusMode !== undefined ? payload.focusMode : prev?.focusMode,
    remoteSpeakersOnStage:
      typeof payload.remoteSpeakersOnStage === 'boolean' ? payload.remoteSpeakersOnStage : prev?.remoteSpeakersOnStage,
    seq: payload.seq,
  };
}

export function shouldClearLivePlayStateOverlayAfterCueRefetch(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry | undefined,
): boolean {
  if (!wsEntry) {
    return false;
  }
  const getSignature = previewPlayStateSignature(liveContextState?.playState?.byPlayer);
  const wsSignature = previewPlayStateSignature(wsEntry.byPlayer);
  if (getSignature === '' || getSignature !== wsSignature) {
    return false;
  }
  if (
    typeof wsEntry.backupSlides === 'boolean' &&
    wsEntry.backupSlides !== (liveContextState?.playState?.backupSlides === true)
  ) {
    return false;
  }
  // The signature ignores html and URLs; keep the overlay while it still carries sources GET lacks.
  return keepWsPreviewSources(liveContextState, wsEntry) === liveContextState;
}

function mergeBackupSlidesOntoPlayState(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry,
  extras?: { presentationTransitionInFlight?: boolean },
): LiveContextStateDto {
  const backupSlides = pickBackupSlides(
    liveContextState?.playState?.seq ?? 0,
    wsEntry.seq,
    liveContextState?.playState?.backupSlides,
    wsEntry.backupSlides,
  );
  return {
    ...liveContextState,
    playState: {
      ...liveContextState?.playState,
      ...(typeof extras?.presentationTransitionInFlight === 'boolean'
        ? { presentationTransitionInFlight: extras.presentationTransitionInFlight }
        : {}),
      ...(typeof backupSlides === 'boolean' ? { backupSlides } : {}),
    },
  };
}

/**
 * The signature ignores html and URLs. A GET `_preview:*` row without them keeps its own layout
 * and takes only the missing sources from the WS row the players were painted from.
 */
function keepWsPreviewSources(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry,
): LiveContextStateDto | undefined {
  const getByPlayer = liveContextState?.playState?.byPlayer;
  if (!liveContextState?.playState || !getByPlayer) {
    return liveContextState;
  }
  let byPlayer: ByPlayer | undefined;
  for (const [key, wsRow] of Object.entries(wsEntry.byPlayer ?? {})) {
    if (!isPreviewByPlayerKey(key) || !wsRow) {
      continue;
    }
    const getRow = getByPlayer[key];
    if (!getRow) {
      if ((wsRow.layers ?? []).some((layer) => contentLayerHasUsableSource(layer as OutputLayer))) {
        byPlayer = { ...(byPlayer ?? getByPlayer), [key]: wsRow };
      }
      continue;
    }
    const getLayers = (getRow.layers ?? []) as OutputLayer[];
    const filled = preserveContentMediaFromCurrent(getLayers, (wsRow.layers ?? []) as OutputLayer[]);
    if (filled.some((layer, index) => layer !== getLayers[index])) {
      byPlayer = { ...(byPlayer ?? getByPlayer), [key]: { ...getRow, layers: filled as typeof getRow.layers } };
    }
  }
  if (!byPlayer) {
    return liveContextState;
  }
  return { ...liveContextState, playState: { ...liveContextState.playState, byPlayer } };
}

function isLiveContextFocusMode(value: string | undefined): value is LiveContextFocusMode {
  return (
    value === LiveContextFocusMode.ROOM || value === LiveContextFocusMode.REMOTE || value === LiveContextFocusMode.BOTH
  );
}

/** Newer play-state seq wins, including an explicit `remoteSpeakersOnStage: false`. */
function withOperatorPictureFlags(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry | undefined,
): LiveContextStateDto | undefined {
  if (!liveContextState || !wsEntry) {
    return liveContextState;
  }
  const wsSeq = wsEntry.seq ?? 0;
  const getSeq = liveContextState.playState?.seq ?? 0;
  if (wsSeq < getSeq) {
    return liveContextState;
  }
  const focusMode = isLiveContextFocusMode(wsEntry.focusMode) ? wsEntry.focusMode : undefined;
  const remoteSpeakersOnStage =
    typeof wsEntry.remoteSpeakersOnStage === 'boolean' ? wsEntry.remoteSpeakersOnStage : undefined;
  if (focusMode === undefined && remoteSpeakersOnStage === undefined) {
    return liveContextState;
  }
  if (liveContextState.focusMode === focusMode && liveContextState.remoteSpeakersOnStage === remoteSpeakersOnStage) {
    return liveContextState;
  }
  return {
    ...liveContextState,
    ...(focusMode !== undefined ? { focusMode } : {}),
    ...(remoteSpeakersOnStage !== undefined ? { remoteSpeakersOnStage } : {}),
  };
}

export function mergeLivePlayStateFromWs(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry | undefined,
): LiveContextStateDto | undefined {
  const merged = mergeLivePlayStateFromWsSignature(liveContextState, wsEntry);
  const withSources = wsEntry ? keepWsPreviewSources(merged, wsEntry) : merged;
  return withOperatorPictureFlags(withSources, wsEntry);
}

function mergeLivePlayStateFromWsSignature(
  liveContextState: LiveContextStateDto | undefined,
  wsEntry: LivePlayStateEntry | undefined,
): LiveContextStateDto | undefined {
  if (!wsEntry || Object.keys(wsEntry.byPlayer ?? {}).length === 0) {
    if (
      wsEntry &&
      (typeof wsEntry.presentationTransitionInFlight === 'boolean' || typeof wsEntry.backupSlides === 'boolean')
    ) {
      return mergeBackupSlidesOntoPlayState(liveContextState, wsEntry, {
        presentationTransitionInFlight: wsEntry.presentationTransitionInFlight,
      });
    }
    return liveContextState;
  }

  const getSignature = previewPlayStateSignature(liveContextState?.playState?.byPlayer);
  const wsSignature = previewPlayStateSignature(wsEntry.byPlayer);
  const getInFlight = liveContextState?.playState?.presentationTransitionInFlight === true;
  const wsInFlight = wsEntry.presentationTransitionInFlight;

  // GET already reflects this WS snapshot — keep the full REST document (physical players included).
  if (getSignature !== '' && getSignature === wsSignature) {
    if (typeof wsInFlight !== 'boolean' || wsInFlight === getInFlight) {
      const backupSlides = pickBackupSlides(
        liveContextState?.playState?.seq ?? 0,
        wsEntry.seq,
        liveContextState?.playState?.backupSlides,
        wsEntry.backupSlides,
      );
      if (backupSlides === liveContextState?.playState?.backupSlides || typeof backupSlides !== 'boolean') {
        return liveContextState;
      }
      return mergeBackupSlidesOntoPlayState(liveContextState, wsEntry);
    }
    // Visible agent on the matched preview means the shield is over — do not re-pin inFlight.
    const inFlight =
      operatorPreviewShowsVisibleAgentLiveKit(liveContextState?.playState?.byPlayer) ||
      operatorPreviewShowsVisibleAgentLiveKit(wsEntry.byPlayer)
        ? false
        : wsInFlight;
    return mergeBackupSlidesOntoPlayState(liveContextState, wsEntry, {
      presentationTransitionInFlight: inFlight,
    });
  }

  // Prefer WS for operator preview. Do not prefer a stale GET "visible agent" over a WS hide —
  // mid-transition GET often still carries the previous take's visible LiveKit; trusting it flashes
  // then disappears when the hide/reveal WS catches up (and can disagree with players).

  return overlayWsPreviewOntoGet(liveContextState, wsEntry);
}
